import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAppContext } from "../AppContext";
import ScopeSwitcher from "../shell/ScopeSwitcher";
import AccountMenu from "../shell/AccountMenu";

const NavItem = ({ item, collapsed }) => (
  <NavLink
    to={item.path}
    end={item.end}
    className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}
    title={collapsed ? item.name : undefined}
  >
    <i className={`nav-item-icon ${item.icon}`} />
    <span className="nav-item-text">{item.name}</span>
  </NavLink>
);

const NavSection = ({ title, items, collapsed, className = "" }) => {
  const visibleItems = items.filter((item) => !item.hidden);
  if (visibleItems.length === 0) return null;

  return (
    <div className={`nav-section ${className}`}>
      {title && <div className="nav-section-title"><span>{title}</span></div>}
      {visibleItems.map((item) => <NavItem key={item.path} item={item} collapsed={collapsed} />)}
    </div>
  );
};

const Menu = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const { currentUser, mobileNavOpen, setMobileNavOpen } = useAppContext();
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("isMenuOpen") === "false");
  const menuRef = useRef(null);
  const indicatorRef = useRef(null);
  const indicatorPlaced = useRef(false);

  const isAdmin = currentUser.role === "superadmin" || currentUser.role === "admin";
  const isSuperAdmin = currentUser.role === "superadmin";
  const isMainEnv = currentUser.environmentId === "main";
  const isSuperEnv = isMainEnv || currentUser.environmentId === "knmt";
  const isKnmtChild = currentUser.parentEnvironment === "knmt";

  const toggleCollapsed = () => {
    localStorage.setItem("isMenuOpen", collapsed ? "true" : "false");
    setCollapsed(!collapsed);
  };

  useEffect(() => {
    document.querySelector("#main")?.classList.toggle("closed", collapsed);
  }, [collapsed, i18n.language]);

  useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname, location.search, setMobileNavOpen]);

  const sections = useMemo(() => [
    {
      key: "home",
      items: [{ name: t("dashboard"), path: "/", end: true, icon: "icon-Dashboard" }],
    },
    {
      key: "risk",
      title: t("menu_risk_management"),
      items: [
        { name: t("risks_plural"), path: "/risks", icon: "icon-Risico" },
        { name: t("measures"), path: "/measures", icon: "icon-Maatregelen" },
        { name: t("soa"), path: "/applicability", icon: "icon-SoA", hidden: !isAdmin },
        { name: t("risk_review"), path: "/risk-review", icon: "icon-risk-review", hidden: isKnmtChild },
        { name: t("rep_menu", "Evaluation"), path: "/prepare-evaluation", icon: "icon-evaluation", hidden: !isAdmin },
        { name: t("menu_copilot"), path: "/copilot", icon: "icon-stars1", hidden: !isAdmin },
      ],
    },
    {
      key: "admin",
      title: t("menu_administration"),
      items: [
        { name: t("domains"), path: "/projects", icon: "icon-Projecten", hidden: !isAdmin },
        { name: t("users"), path: "/users", icon: "icon-Gebruikers", hidden: !isAdmin },
        { name: t("organizations"), path: "/organizations", icon: "icon-Organisaties", hidden: !isSuperAdmin },
        { name: t("risk_templates"), path: "/risktemplates", icon: "icon-Template", hidden: !isSuperAdmin },
      ],
    },
    {
      key: "platform",
      title: t("menu_platform"),
      items: [
        { name: t("environments"), path: "/environments", icon: "icon-sitemap1", hidden: !isSuperAdmin || !isSuperEnv },
        { name: t("statistics"), path: "/activities", icon: "icon-statistics", hidden: !isSuperAdmin || !isSuperEnv },
        { name: t("requests"), path: "/requests", icon: "icon-requests", hidden: !isSuperAdmin || !isMainEnv },
        { name: t("mailing.title"), path: "/mailing", icon: "icon-mail", hidden: !isSuperAdmin || !isMainEnv },
      ],
    },
    {
      key: "help",
      className: "nav-section-help",
      items: [{ name: t("help.title"), path: "/help", icon: "icon-idea" }],
    },
  ], [t, isAdmin, isSuperAdmin, isSuperEnv, isMainEnv, isKnmtChild]);

  // A single indicator slides between items so the active "tab" (including its
  // concave corners) moves as one piece instead of cross-fading per item.
  const placeIndicator = useCallback((animate) => {
    const indicator = indicatorRef.current;
    const active = menuRef.current?.querySelector(".nav-item.active");
    if (!indicator) return;
    if (!active) {
      indicator.classList.remove("visible");
      indicatorPlaced.current = false;
      return;
    }
    const instant = !animate || !indicatorPlaced.current;
    if (instant) indicator.classList.add("instant");
    indicator.style.transform = `translateY(${active.offsetTop}px)`;
    indicator.style.height = `${active.offsetHeight}px`;
    indicator.classList.add("visible");
    if (instant) {
      void indicator.offsetHeight;
      indicator.classList.remove("instant");
    }
    indicatorPlaced.current = true;
  }, []);

  useLayoutEffect(() => {
    placeIndicator(true);
  }, [location.pathname, placeIndicator]);

  useLayoutEffect(() => {
    placeIndicator(false);
  }, [sections, collapsed, i18n.language, placeIndicator]);

  useEffect(() => {
    const onResize = () => placeIndicator(false);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [placeIndicator]);

  return (
    <>
      <nav className={`${collapsed ? "closed" : ""} ${mobileNavOpen ? "mobile-open" : ""}`} aria-label={t("shell.main_navigation")}>
        <div className="nav-top">
          <NavLink to="/" className="nav-logo" aria-label="Risqui">
            <i className="icon-risqui" />
          </NavLink>
          <button
            type="button"
            className="nav-collapse"
            onClick={toggleCollapsed}
            title={collapsed ? t("shell.expand_menu") : t("shell.collapse_menu")}
            aria-label={collapsed ? t("shell.expand_menu") : t("shell.collapse_menu")}
          >
            <i className="icon-chevron-left" />
          </button>
          <button type="button" className="nav-mobile-close" onClick={() => setMobileNavOpen(false)} aria-label={t("close")}>
            <i className="icon-close" />
          </button>
        </div>

        <div className="nav-scope">
          <ScopeSwitcher collapsed={collapsed} />
        </div>

        <div id="menu" ref={menuRef}>
          <span className="nav-indicator" ref={indicatorRef} aria-hidden="true" />
          {sections.map((section) => (
            <NavSection key={section.key} title={section.title} items={section.items} collapsed={collapsed} className={section.className} />
          ))}
        </div>

        <div id="menu-bottom">
          <AccountMenu collapsed={collapsed} />
        </div>
      </nav>

      <div className={`shell-corner top ${collapsed ? "closed" : ""}`} aria-hidden="true" />
      <div className={`shell-corner bottom ${collapsed ? "closed" : ""}`} aria-hidden="true" />
      <div className={`nav-backdrop ${mobileNavOpen ? "show" : ""}`} onClick={() => setMobileNavOpen(false)} aria-hidden="true" />
    </>
  );
};

export default Menu;
